import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import shieldAlt from '@iconify/icons-fa-solid/shield-alt';
import boltIcon from '@iconify/icons-fa-solid/bolt';
import cartPlus from '@iconify/icons-fa-solid/cart-plus';
import handHoldingUsd from '@iconify/icons-fa-solid/hand-holding-usd';
import { storage } from '@/utils';
import styles from './P2PTradePanel.module.css';

const TRADE_PATH = '/trade';
const LOGIN_PATH = '/login?modalType=auth-signin';
const PARTICLE_COUNT = 10;

/* ─── Main P2P Symbol — two peers exchanging USDT ─── */
const P2PSymbol = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="p2pGradA" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#F5D76E" />
        <stop offset="100%" stopColor="#B8962E" />
      </linearGradient>
      <linearGradient id="p2pGradB" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#26A17B" />
        <stop offset="100%" stopColor="#16A34A" />
      </linearGradient>
      <radialGradient id="p2pCoinGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F5D76E" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#F5D76E" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Left peer */}
    <circle cx="9" cy="24" r="6" stroke="url(#p2pGradA)" strokeWidth="1.6" fill="rgba(245, 215, 110, 0.08)" />
    <circle cx="9" cy="21.5" r="2" fill="url(#p2pGradA)" />
    <path d="M4.5 28.5 C 5.5 26.2, 12.5 26.2, 13.5 28.5" stroke="url(#p2pGradA)" strokeWidth="1.6" strokeLinecap="round" fill="none" />

    {/* Right peer */}
    <circle cx="39" cy="24" r="6" stroke="url(#p2pGradB)" strokeWidth="1.6" fill="rgba(38, 161, 123, 0.08)" />
    <circle cx="39" cy="21.5" r="2" fill="url(#p2pGradB)" />
    <path d="M34.5 28.5 C 35.5 26.2, 42.5 26.2, 43.5 28.5" stroke="url(#p2pGradB)" strokeWidth="1.6" strokeLinecap="round" fill="none" />

    {/* Exchange arrows */}
    <path d="M17 20 L 30 20" stroke="url(#p2pGradA)" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M28 17.5 L 30.5 20 L 28 22.5" stroke="url(#p2pGradA)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <path d="M31 28 L 18 28" stroke="url(#p2pGradB)" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M20 25.5 L 17.5 28 L 20 30.5" stroke="url(#p2pGradB)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Center USDT coin */}
    <circle cx="24" cy="24" r="9" fill="url(#p2pCoinGlow)" />
    <circle cx="24" cy="24" r="5.2" fill="#0f1520" stroke="url(#p2pGradA)" strokeWidth="1.3" />
    <text x="24" y="26.4" textAnchor="middle" fontSize="4.4" fontWeight="900" fill="#F5D76E" fontFamily="system-ui, sans-serif">₮</text>
  </svg>
);

/* ─── Golden Diamond Badge — premium highlight ─── */
const GoldenBadge = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="goldBadgeGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#FFE98A" />
        <stop offset="45%" stopColor="#F5D76E" />
        <stop offset="100%" stopColor="#B8962E" />
      </linearGradient>
      <linearGradient id="goldBadgeShine" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFF5C8" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#FFF5C8" stopOpacity="0" />
      </linearGradient>
      <radialGradient id="goldBadgeGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F5D76E" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#F5D76E" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Outer glow */}
    <circle cx="20" cy="20" r="18" fill="url(#goldBadgeGlow)" />

    {/* Diamond shape */}
    <path
      d="M20 5 L32 17 L20 35 L8 17 Z"
      fill="url(#goldBadgeGrad)"
      stroke="#B8962E"
      strokeWidth="0.8"
      strokeLinejoin="round"
    />

    {/* Top facet */}
    <path d="M20 5 L14 17 L20 17 L26 17 Z" fill="url(#goldBadgeGrad)" opacity="0.85" />
    <path d="M8 17 L14 17 M20 17 L20 35 M26 17 L32 17" stroke="#8B6E1E" strokeWidth="0.6" opacity="0.6" />
    <path d="M20 5 L14 17 M20 5 L26 17" stroke="#8B6E1E" strokeWidth="0.6" opacity="0.5" />

    {/* Shine highlight */}
    <path d="M20 6 L25 16 L22 16 Z" fill="url(#goldBadgeShine)" />

    {/* Center star */}
    <path
      d="M20 13 L21 17 L25 17 L22 19.5 L23 23.5 L20 21 L17 23.5 L18 19.5 L15 17 L19 17 Z"
      fill="#FFF5C8"
      opacity="0.95"
    />
  </svg>
);

const P2PTradePanel = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const logged =
      storage.getKey('logged') === true ||
      localStorage.getItem('logged') === 'true';
    setIsLoggedIn(!!logged);
  }, []);

  const goToTrade = useCallback(
    () => navigate(isLoggedIn ? TRADE_PATH : LOGIN_PATH),
    [navigate, isLoggedIn],
  );

  const particles = useMemo(
    () =>
      Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: 1 + Math.random() * 2.5,
        dur: 2.5 + Math.random() * 3.5,
        delay: Math.random() * 3,
        hue: Math.random() > 0.5 ? 'gold' : 'green',
      })),
    [],
  );

  return (
    <div className={styles.panel} onClick={goToTrade} role="button" tabIndex={0}>
      <div className={styles.borderGlow} />
      <div className={styles.bgLayer} />
      <div className={styles.meshGrid} />

      {/* Floating golden premium badge */}
      <div className={styles.goldBadgeFloat}>
        <GoldenBadge className={styles.goldBadgeSvg} />
        <span className={styles.goldBadgeLabel}>PREMIUM</span>
      </div>

      {particles.map((p) => (
        <div
          key={p.id}
          className={`${styles.particle} ${p.hue === 'green' ? styles.particleGreen : ''}`}
          style={{
            left: `${p.x}%`,
            bottom: `${p.y}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animation: `floatUp ${p.dur}s ease-in-out ${p.delay}s infinite`,
          }}
        />
      ))}

      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconWrap}>
            <div className={styles.iconRingPulse} />
            <img style={{objectFit:'contain'}} src="/assets/p2p-icon.png" alt="P2P" className={styles.p2pSvg} />
          </div>

          <div className={styles.headText}>
            <div className={styles.titleRow}>
              <span className={styles.title}>P2P TRADE</span>
              <span className={styles.titleBadge}>
                <span className={styles.liveDot} />
                LIVE
              </span>
            </div>
            <span className={styles.subtitle}>
              PEER&nbsp;·&nbsp;TO&nbsp;·&nbsp;PEER&nbsp;&nbsp;CRYPTO&nbsp;EXCHANGE
            </span>
          </div>
        </div>

        {/* Highlighted Stats Strip */}
        <div className={styles.highlightStrip}>
          <div className={styles.highlightItem}>
            <span className={styles.highlightValue}>0%</span>
            <span className={styles.highlightLabel}>Trading Fee</span>
          </div>
          <span className={styles.highlightDivider} />
          <div className={styles.highlightItem}>
            <span className={styles.highlightValueGreen}>~30s</span>
            <span className={styles.highlightLabel}>Avg Release</span>
          </div>
          <span className={styles.highlightDivider} />
          <div className={styles.highlightItem}>
            <span className={styles.highlightValueGold}>24/7</span>
            <span className={styles.highlightLabel}>Support</span>
          </div>
        </div>

        {/* Buy / Sell buttons */}
        <div className={styles.actions}>
          <button
            className={`${styles.chip} ${styles.chipBuy}`}
            onClick={(e) => { e.stopPropagation(); goToTrade(); }}
            type="button"
          >
            <span className={styles.chipShimmer} />
            <span className={styles.chipGlow} />
            <Icon icon={cartPlus} width={13} height={13} className={styles.chipIcon} />
            <span className={styles.chipLabel}>Buy USDT</span>
            <span className={styles.chipArrow}>→</span>
          </button>

          <button
            className={`${styles.chip} ${styles.chipSell}`}
            onClick={(e) => { e.stopPropagation(); goToTrade(); }}
            type="button"
          >
            <span className={styles.chipShimmer} />
            <span className={styles.chipGlow} />
            <Icon icon={handHoldingUsd} width={13} height={13} className={styles.chipIcon} />
            <span className={styles.chipLabel}>Sell USDT</span>
            <span className={styles.chipArrow}>→</span>
          </button>
        </div>

        {/* Trust strip */}
        <div className={styles.trustStrip}>
          <span className={styles.trustItem}>
            <Icon icon={shieldAlt} width={9} height={9} className={`${styles.trustIcon} ${styles.trustGreen}`} />
            Escrow Protected
          </span>
          <span className={styles.trustDot} />
          <span className={styles.trustItem}>
            <Icon icon={boltIcon} width={9} height={9} className={`${styles.trustIcon} ${styles.trustGold}`} />
            Instant Release
          </span>
          <span className={styles.trustDot} />
          <span className={styles.trustMuted}>UPI · IMPS · Bank · GPay · PhonePe</span>
        </div>
      </div>
    </div>
  );
};

export default P2PTradePanel;